import { useState } from "react";
import { IEditPostModalProps } from "../../../components/BrandedCommunity/EditPostModal/EditPostModal.types";
import { IPostAudio } from "../../../components/BrandedCommunity/Post/Post.types";
import { POST_TYPE_IDS_ENUM } from "../../../constants/BrandedCommunity/Post";
import { checkIsArrayEmpty } from "../../common/dataTypes/array";
import { IUseMediaInEditPostModalProps } from "./useMediaInEditPostModal.types";

export interface IAudioSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseWithoutSelecting: () => void;
  onAddAudio: (audio: IPostAudio) => void;
  audioUploading: boolean;
  setAudioUploading: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface IUseAudioInEditPostModalReturn {
  onAddPostTypeAudioClick: () => void;
  postAudioProps: IEditPostModalProps["postAudioProps"];
  audioSelectorProps: IAudioSelectorProps;
}

export const useAudioInEditPostModal = ({
  postData,
  setPostData,
  disablePostTypeDataEdit,
  onRemovePostTypeMedia,
  onCloseSelectorWithoutSelecting,
  onAddPostTypeMediaSuccess,
}: IUseMediaInEditPostModalProps): IUseAudioInEditPostModalReturn => {
  const [audioSelectorIsOpen, setAudioSelectorIsOpen] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);

  const onAddPostTypeAudioClick = () => {
    setAudioSelectorIsOpen(true);
  };

  const onCloseAudioSelector = () => {
    setAudioSelectorIsOpen(false);
  };

  const audios: IPostAudio[] = postData?.metadata?.post_audios ?? [];

  const onAddAudio = (newAudio: IPostAudio) => {
    const postAudios = postData?.metadata?.post_audios ?? [];
    const updatedAudios = [...postAudios, newAudio];
    setPostData({
      ...postData,
      post_type: POST_TYPE_IDS_ENUM.AUDIO,
      metadata: {
        ...postData?.metadata,
        post_audios: updatedAudios,
      },
    });
    onAddPostTypeMediaSuccess();
    onCloseAudioSelector();
  };

  const onClose = onCloseAudioSelector;
  const onCloseWithoutSelecting = () => {
    onCloseSelectorWithoutSelecting();
    onClose();
  };

  const handleDeleteAudio = (index: number = 0) => {
    const postAudios = postData?.metadata?.post_audios;
    if (postAudios) {
      const updatedAudios = [...postAudios];
      updatedAudios.splice(index, 1);
      setPostData({
        ...postData,
        post_type: checkIsArrayEmpty(updatedAudios)
          ? POST_TYPE_IDS_ENUM.TEXT
          : POST_TYPE_IDS_ENUM.AUDIO,
        metadata: {
          ...postData.metadata,
          post_audios: updatedAudios,
        },
      });

      if (checkIsArrayEmpty(updatedAudios)) {
        onRemovePostTypeMedia();
      }
    }
  };

  const postTypeAudioIsAdded =
    !checkIsArrayEmpty(audios) &&
    postData.post_type === POST_TYPE_IDS_ENUM.AUDIO;

  return {
    onAddPostTypeAudioClick,
    postAudioProps: {
      audios,
      showAudio: postTypeAudioIsAdded,
      isRemovable: !disablePostTypeDataEdit,
      onRemove: handleDeleteAudio,
    },
    audioSelectorProps: {
      isOpen: audioSelectorIsOpen,
      onClose,
      onCloseWithoutSelecting,
      onAddAudio,
      audioUploading,
      setAudioUploading,
    },
  };
};
